/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled'
import { DeleteOutlined } from '@ant-design/icons'
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import Text from '../../Text'
import Button from '../../Button'
import { SelectInput } from '../../SelectInput'
import { TextInput } from '../../TextInput'
import ProgressHeader from '../components/ProgressHeader'
import { useOnboardingWizard } from '../context/OnboardingWizardContext'
import colors from '@sportninja/common/constants/appColors'
import { trackOnboardingSportsSetup } from 'src/utils/analytics'

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const ContentContainer = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SportsListWrapper = styled.div`
  overflow-y: auto;
  max-height: 45vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 5px;

  @media (max-width: 768px) {
    max-height: 40vh;
    flex: 1;
    min-height: 200px;
  }
`

const InputRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`

const MobileInputWrapper = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: flex;
    align-items: flex-end;
    gap: 8px;
  }
`

const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const AddMoreButton = styled.div`
  align-self: center;
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${colors.ERROR_200};
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  margin-top: 24px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    margin-top: 0;
    align-self: flex-end;
    margin-bottom: 8px;
  }
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`

const NavigationButtons = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${colors.PRIMARY};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

interface SportEntry {
  id: string
  sport_id: string
  generic_sport_id: string | null
  leagueName: string
  is_generic: boolean
}

/**
 * Sports Selection step component - Step 2 of the onboarding wizard
 * Allows users to select sports and configure league names for their organization
 */
const SportsSelection: React.FC = () => {
  const {
    previousStep,
    nextStep,
    skipWizard,
    sports,
    setSportsData,
    sportsData,
    genericSportId,
  } = useOnboardingWizard()

  // Form state - initialize from context data if available
  const [sportsState, setSportsState] = useState<SportEntry[]>(() => {
    if (sportsData && sportsData.length > 0) {
      return sportsData
    }
    return [
      {
        id: Date.now().toString(),
        sport_id: '',
        generic_sport_id: null,
        leagueName: '',
        is_generic: false,
      },
    ]
  })

  const [fieldErrors, setFieldErrors] = useState<any>({})
  const [customError, setCustomError] = useState<any>({})

  // State and ref for Tab-to-select functionality
  const [dropdownOpenIndex, setDropdownOpenIndex] = useState<number | null>(
    null
  )
  const sportSearchRef = useRef('')

  // Sync local state with context data when it changes (e.g., when restored from localStorage)
  useEffect(() => {
    if (
      sportsData &&
      sportsData.length > 0 &&
      sportsState.length === 1 &&
      !sportsState[0].sport_id
    ) {
      setSportsState(sportsData)
    }
  }, [sportsData, sportsState])

  // Create select options similar to GeneralForm structure
  const selectOptions = [
    {
      label: (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              font-size: 12px;
              font-weight: 600;
              color: ${colors.PRIMARY};
              text-transform: uppercase;
            `}
          >
            Full Stats
          </span>
        </div>
      ),
      title: 'fullStats',
      options: sports
        .filter((sport) => !sport.is_generic)
        .map((option) => ({
          value: option.sport_id,
          label: option.name_full,
          is_generic: false,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
    {
      label: (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              font-size: 12px;
              font-weight: 600;
              color: ${colors.PRIMARY};
              text-transform: uppercase;
            `}
          >
            Basic Stats
          </span>
        </div>
      ),
      title: 'basicStats',
      options: sports
        .filter((sport) => sport.is_generic)
        .map((option) => ({
          value: option.generic_sport_id || '',
          label: `${option.name_full} (Basic)`,
          is_generic: true,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
  ]

  // Filter function for grouped options
  const filterOptionGroup = useCallback(
    (search: string, optionOrGroup: any) => {
      const isGroup = Array.isArray(optionOrGroup.options)
      if (isGroup) {
        return false
      }
      return optionOrGroup.label.toLowerCase().includes(search.toLowerCase())
    },
    []
  )

  // Flatten options for Tab-to-select matching
  const flatSportOptions = useMemo(
    () => selectOptions.flatMap((group) => group.options),
    [selectOptions]
  )

  // Tab-to-select handler for sport dropdown
  const handleSportKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (
        e.key === 'Tab' &&
        dropdownOpenIndex === index &&
        sportSearchRef.current
      ) {
        const matching = flatSportOptions.find((opt) =>
          opt.label.toLowerCase().includes(sportSearchRef.current.toLowerCase())
        )
        if (matching) {
          e.preventDefault()
          // Select the matching option
          if (matching.is_generic) {
            updateSport(index, 'sport_id', genericSportId || '')
            updateSport(index, 'generic_sport_id', matching.value)
          } else {
            updateSport(index, 'sport_id', matching.value)
          }
          setDropdownOpenIndex(null)
          sportSearchRef.current = ''
        }
      }
    },
    [dropdownOpenIndex, flatSportOptions, genericSportId]
  )

  const updateSport = (
    index: number,
    field: 'sport_id' | 'generic_sport_id' | 'leagueName',
    value: string
  ) => {
    setSportsState((prev) =>
      prev.map((sport, i) => {
        if (i === index) {
          if (field === 'sport_id') {
            // When selecting a sport_id, clear generic_sport_id and set is_generic to false
            return {
              ...sport,
              sport_id: value,
              generic_sport_id: null,
              is_generic: false,
            }
          } else if (field === 'generic_sport_id') {
            // When selecting a generic_sport_id, set is_generic to true
            return {
              ...sport,
              generic_sport_id: value,
              is_generic: true,
            }
          } else {
            return { ...sport, [field]: value }
          }
        }
        return sport
      })
    )

    // Clear errors when user starts typing
    if (fieldErrors[`${field}_${index}`]) {
      setFieldErrors((prev) => ({
        ...prev,
        [`${field}_${index}`]: undefined,
      }))
    }
  }

  const addMoreSport = () => {
    setSportsState((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        sport_id: '',
        generic_sport_id: null,
        leagueName: '',
        is_generic: false,
      },
    ])
  }

  const removeSport = (index: number) => {
    setSportsState((prev) => prev.filter((_, i) => i !== index))
  }

  const handleNext = () => {
    // Filter out empty items - remove sports without a selected sport or without a name
    const validSports = sportsState.filter(
      (sport) =>
        (sport.sport_id || sport.generic_sport_id) && sport.leagueName.trim()
    )

    if (validSports.length === 0) {
      setCustomError({
        general: 'Please add at least one sport and league name',
      })
      return
    }

    // Clear errors and proceed
    setFieldErrors({})
    setCustomError({})

    // Track the sports setup completion with sport count
    trackOnboardingSportsSetup(validSports.length)

    // Save only the valid sports data to the context (filtered out empty items)
    setSportsData(validSports)

    // Proceed to the next step
    nextStep()
  }

  const canProceed = sportsState.some(
    (sport) => (sport.sport_id || sport.generic_sport_id) && sport.leagueName
  )

  return (
    <StepContainer>
      <ProgressHeader currentStep={2} />

      <ContentContainer>
        <TitleSection>
          <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
            What Sports Does Your League Run?
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Add all the sports your league runs so we can help organize your
            setup.
          </Text>
        </TitleSection>

        <FormContainer>
          <SportsListWrapper>
            {sportsState.map((sport, index) => (
              <InputRow key={sport.id}>
                <InputWrapper>
                  <SelectInput
                    placeholder='Select Sport'
                    label='Sport *'
                    disabled={false}
                    options={selectOptions}
                    filterOption={filterOptionGroup}
                    name={`sport_${index}`}
                    value={
                      sport.is_generic
                        ? sport.generic_sport_id || ''
                        : sport.sport_id
                    }
                    showSearch={true}
                    onChange={(value) => {
                      // Find the selected sport to determine if it's generic
                      const selectedOption = selectOptions
                        .flatMap((group) => group.options)
                        .find((option) => option.value === value)

                      if (selectedOption?.is_generic) {
                        // If the selected sport is generic, set the sport_id to the generic sport id
                        updateSport(index, 'sport_id', genericSportId || '')
                        updateSport(index, 'generic_sport_id', value)
                      } else {
                        updateSport(index, 'sport_id', value)
                      }
                    }}
                    fieldErrors={fieldErrors}
                    customError={customError}
                    open={dropdownOpenIndex === index}
                    onDropdownVisibleChange={(open) =>
                      setDropdownOpenIndex(open ? index : null)
                    }
                    onSearch={(val) => {
                      sportSearchRef.current = val
                    }}
                    onKeyDown={(e) => handleSportKeyDown(e, index)}
                  />
                </InputWrapper>

                <MobileInputWrapper>
                  <InputWrapper>
                    <TextInput
                      name={`leagueName_${index}`}
                      value={sport.leagueName}
                      setValue={(value) =>
                        updateSport(index, 'leagueName', value)
                      }
                      fieldErrors={fieldErrors}
                      customError={customError}
                      label='Sub-League Name *'
                      placeholder='Enter Sub-League Name'
                      maxLength={100}
                    />
                  </InputWrapper>

                  {sportsState.length > 1 && (
                    <RemoveButton onClick={() => removeSport(index)}>
                      <DeleteOutlined />
                    </RemoveButton>
                  )}
                </MobileInputWrapper>
              </InputRow>
            ))}
          </SportsListWrapper>

          <AddMoreButton>
            <div
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 8px;
                cursor: pointer;
              `}
              onClick={addMoreSport}
            >
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.PRIMARY}
              >
                + Add More Sports
              </Text>
            </div>
          </AddMoreButton>
        </FormContainer>

        {customError.general && (
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.ERROR_300}
            style={'text-align: center;'}
          >
            {customError.general}
          </Text>
        )}

        <ActionsContainer>
          <NavigationButtons>
            <Button
              text='BACK'
              onClick={previousStep}
              variant='secondary'
              uppercase
              style={css`
                flex: 1;
              `}
            />
            <Button
              text='NEXT'
              onClick={handleNext}
              variant='primary'
              uppercase
              disabled={!canProceed}
              style={css`
                flex: 1;
              `}
            />
          </NavigationButtons>

          <SkipLink onClick={skipWizard}>Skip Setup Wizard</SkipLink>
        </ActionsContainer>
      </ContentContainer>
    </StepContainer>
  )
}

export default SportsSelection
